import { is } from 'date-fns/locale';
import {
    IQueryContext,
    IQueryContextDatasourceColumn,
    QueryContextSettingID,
} from '../query-context/models/IQueryContext';
import { splitGroupStringToArr } from 'src/app/utils/query/sql';

export interface IDataLoaderUpliftHelperOptions {
    /** Define to calculate uplift metrics */
    calculateUplift?: {
        /**
         * List of uplift metrics to calculate uplift against reference split group.
         * Uplift metrics are calculated as (metric / reference_metric) - 1.
         * Metrics are calculated in js code instead of SQL.
         * If empty, no uplift metrics will be calculated.
         *
         */
        upliftMetrics: string[];
        /** Setting id for uplift reference segment */
        upliftReferenceSegmentSettingId: QueryContextSettingID;
        /** xAxisColumn. @default `day_no` */
        xAxisColumn?: string;
    };
}

export class DataLoaderUpliftHelper {
    static processUpliftMetrics(
        options: IDataLoaderUpliftHelperOptions,
        queryContext: IQueryContext,
        data: any
    ) {
        if (options.calculateUplift !== undefined) {
            // throw error if uplift metrics are not undefined and empty
            if (options.calculateUplift.upliftMetrics.length === 0) {
                throw new Error(
                    'Uplift metrics are empty. Please provide uplift metrics to calculate uplift.'
                );
            }
            // calculate uplift metrics
            if (options.calculateUplift.upliftMetrics.length > 0) {
                const referenceSegment =
                    queryContext.settings[
                        options.calculateUplift.upliftReferenceSegmentSettingId
                    ];
                // if split by SEGMENT is applied
                const isSegmentSplit = queryContext.datasourceColumns.some(
                    (segment: IQueryContextDatasourceColumn) =>
                        segment.id === 'SEGMENT' && segment.state.isSplitApplied
                );
                addUpliftMetricsValues(
                    data,
                    options.calculateUplift.upliftMetrics,
                    options.calculateUplift.xAxisColumn ?? 'day_no',
                    referenceSegment as string,
                    isSegmentSplit
                );
            }
        }
    }
}

/**
 * Process the data for uplift metrics
 * If `split_group` is present in data then uplift metrics are calculated against reference `split_group`
 * If `split_group` is not present then uplift metrics are `null`
 */
function addUpliftMetricsValues(
    data: any,
    uplift_metrics: string[],
    xAxisColumn: string,
    referenceSplitGroupPart?: string,
    /** True if split by SEGMENT is applied */
    isSegmentSplit?: boolean
) {
    // calculate uplift metrics
    if (
        isSegmentSplit &&
        data[0] &&
        'split_group' in data[0] &&
        referenceSplitGroupPart
    ) {
        // data contains split_group
        // split_group is a string which could contain referenceSplitGroup
        // create a map with all split_groups that contain referenceSplitGroup, but referenceSplitGroup in split_group must be replaced with empty string
        // it will be used as a key in later steps

        const distinctSplitGroups = new Set<string>(
            data.map((item: { split_group: string }) => item.split_group)
        );

        const splitGroupInfo = new Map<string, any>();

        // get index of referenceSplitGroup in split_group
        let referenceSplitGroupIndices = Array.from(distinctSplitGroups)
            .map((splitGroup) => {
                return splitGroup.split(', ').indexOf(referenceSplitGroupPart);
            })
            .filter((index) => index !== -1)
            // reduce to distinct elements
            .filter((value, index, self) => self.indexOf(value) === index);

        if (referenceSplitGroupIndices.length > 1) {
            throw new Error(
                `Reference split group ${referenceSplitGroupPart} is not unique.`
            );
        }
        const referenceSplitGroupIndex = referenceSplitGroupIndices[0];

        // at the end of this step, referencesMap will contain all split_groups with its corresponding reference split group
        distinctSplitGroups.forEach((splitGroup) => {
            // get reference split group for the split group
            const referenceSplitGroup = splitGroupStringToArr(splitGroup)
                .map((splitGroupPart, index) =>
                    index === referenceSplitGroupIndex
                        ? referenceSplitGroupPart
                        : splitGroupPart
                )
                .join(', ');
            splitGroupInfo.set(splitGroup, {
                referenceSplitGroup,
                data: data.filter(
                    (item: { split_group: string }) =>
                        item.split_group === referenceSplitGroup
                ),
            });
        });

        // for each row calculate uplift metrics
        data.forEach((row: any) => {
            if (
                row.split_group !==
                splitGroupInfo.get(row.split_group).referenceSplitGroup
            ) {
                uplift_metrics.forEach((metric) => {
                    // get reference value for the day
                    // if there is not reference value for the day then uplift metric is null
                    const referenceRow =
                        splitGroupInfo
                            .get(row.split_group)
                            .data.find(
                                (refRow: any) =>
                                    row[xAxisColumn] === refRow[xAxisColumn]
                            ) ?? {};
                    if (row[metric] === 0 && referenceRow[metric] === 0) {
                        row[metric + '_uplift'] = 0;
                    } else if (
                        row[metric] === undefined ||
                        referenceRow[metric] === undefined
                    ) {
                        row[metric + '_uplift'] = null;
                    } else {
                        row[metric + '_uplift'] =
                            (row[metric] ?? null) /
                                (referenceRow[metric] ?? null) -
                            1;
                    }
                });
            }
            // if row is reference group then uplift metrics are null
            else {
                uplift_metrics.forEach((metric) => {
                    row[metric + '_uplift'] = null;
                });
            }
        });
    }
    // add null uplift metrics with
    else {
        uplift_metrics.forEach((metric) => {
            data.forEach((row: any) => {
                row[metric + '_uplift'] = null;
            });
        });
    }
}
